import { afterNextRender, ChangeDetectionStrategy, Component, computed, inject, signal, TemplateRef, viewChild } from '@angular/core';
import { CacheService } from '../../../auth.service';
import { IOfferCard } from '../../../types';
import { ProfileService } from '../profile.service';
import { MatIconModule } from '@angular/material/icon';
import { ItemGridComponent } from '../../item-grid/item-grid.component';
import { MatButtonModule } from '@angular/material/button';
import { IRedemptionCard, RedemptionCardComponent } from "../../../brands/redemption-card/redemption-card.component";
import { ConfirmPopup } from '../../../confirm.popup';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { MatError, MatHint } from '@angular/material/form-field';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'profile-redemptions',
  templateUrl: 'redemptions.component.html',
  styleUrls: ['redemptions.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ItemGridComponent, MatIconModule, MatButtonModule, RedemptionCardComponent, ReactiveFormsModule, MatError, MatHint, NgTemplateOutlet]
})
export class ProfileRedemptionsComponent {
  cache = inject(CacheService);
  service = inject(ProfileService);
  confirm = inject(ConfirmPopup);
  toast = inject(ToastrService);
  
  editTemplate = viewChild<TemplateRef<any>>('editor');
  loader = signal<boolean>(false);
  maxxed = signal<boolean>(false);
  offers = signal<IRedemptionCard[]>([]);

  savings = new FormControl<number>(0, { validators: [Validators.required, Validators.min(0.1)], nonNullable: true });

  total = computed(() => this.offers() ? this.offers().length : -1);

  constructor() { 
    afterNextRender(() => {
      this.loadResults();
    })
  }

  loadResults(){
    // console.log('load', this.maxxed(), this.loader());
    if(this.maxxed() || this.loader())
      return;

    this.loader.set(true);
    
    this.service.getRedemptions(this.total(), 20).subscribe({
      next: ({ redemptions, plans }) => {
          this.cache.registerPlans(plans);
          this.maxxed.set(redemptions.length < 20);
          this.offers.update(existingOffers => ([...existingOffers, ...redemptions]));
          this.loader.set(false);
      },
      error: error => {
        error = error.error||error;
        this.offers.set([]);
        this.loader.set(false);
        this.service.toast.error(error.message);
      }
    });
  }

  requestEdit(_id: string){
    const redemption = this.offers().find(o => o.redemption == _id);
    if(redemption){
      this.savings.reset(redemption.actual_savings);
      this.confirm.open('Update Savings', this.editTemplate()!, 'Savings Editor', [
        {
          label: 'Save',
          paint: 'primary',
          callback: (dialogRef) => {
            if(this.savings.valid){
              this.loader.set(true);
              this.service.editRedemption(_id, this.savings.value).subscribe({
                next: result => {
                  
                  const user = this.service.profile();
  
                  if(user?.stats){
                    user.stats[0].offers.year_savings = result.year_savings;
                    user.stats[0].offers.total_savings = result.total_savings;
                    this.service.profile.set({...user});
                  }
  
                  this.toast.success('Redemption savings have been updated');
  
                  dialogRef.close();
                  this.offers.set([]);
                  this.loader.set(false);
                  this.maxxed.set(false);
                  this.loadResults();
                },
                error: error => {
                  error = error.error||error;
                  this.loader.set(false);
                  this.service.toast.error(error.message);
                }
              })
            } else {
              this.savings.markAsTouched();
              this.savings.markAsDirty();
            }
          },
        }
      ], 'close');
    }
  }

}
